import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

import Snackbar from "../../common/Snackbar";

import CommunityForm from "./form";
import OnboardingForm from "./form/OnboardingForm";
import StripePaymentsPanel from "./form/StripePaymentsPanel";
import ShopForm from "./form/ShopForm";
import PaywallForm from "./form/PaywallForm";
import DistributionForm from "./form/DistributionForm";
import AIAccessForm from "./form/AIAccessForm";
import LandingForm from "./form/LandingForm";
import BookingForm from "./form/BookingForm";
import BrandForm from "./form/BrandForm";
import { Community } from "../../../types/Community";
import { Shop } from "../../../types/Shop";
import { SubscriptionLandingInfo } from "../../../types/Paywall";
import { Distribution } from "../../../types/Distribution";
import { useDistribution } from "../../../database/distribution/useDistribution";
import { updateDistribution } from "../../../database/distribution/updateDistribution";
import { useAllCommunities } from "../../../database/community/getAllCommunities";
import { createCommunity } from "../../../database/community/createCommunity";
import { updateCommunity } from "../../../database/community/updateCommunity";
import { deleteCommunity } from "../../../database/community/deleteCommunity";
import {
  removeQuestionnaire,
  saveQuestionnaire,
  useQuestionnaire,
} from "../../../database/questionnaire/useQuestionnaire";
import { uploadQuestionnaire } from "../../../database/questionnaire/uploadQuestionnaire";
import { useAllShops } from "../../../database/shop/getAllShops";
import { createShop } from "../../../database/shop/createShop";
import { updateShop } from "../../../database/shop/updateShop";
import { deleteShop } from "../../../database/shop/deleteShop";
import { useAllPaywalls } from "../../../database/paywall/getAllPaywalls";
import { createPaywall } from "../../../database/paywall/createPaywall";
import { updatePaywall } from "../../../database/paywall/updatePaywall";
import { deletePaywall } from "../../../database/paywall/deletePaywall";
import StoreUser from "../../../types/StoreUser";
import { Questionnaire } from "../../../types/Questionnaire";
import useAllPlans from "../../../database/plans/getAllPlans";

import {
  DesignSystemSurface,
  PageHeader,
  Card,
} from "../../../design-system";
import { IconSettings } from "../../../design-system/icons/icons";

const Page = styled.div`
  min-height: calc(100vh - 104px);
  padding: var(--space-6) var(--space-6) var(--space-16);
`;

const PageInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const TabBar = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 4px;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-6);
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    color: var(--color-text);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  ${(p) =>
    p.$active &&
    css`
      background: var(--color-surface);
      color: var(--color-text);
      font-weight: var(--font-weight-semibold);
      box-shadow: var(--shadow-xs);
    `}
`;

type Props = {
  user: StoreUser;
};

const TABS: { id: number; label: string }[] = [
  { id: 0, label: "General" },
  { id: 8, label: "Brand" },
  { id: 1, label: "Onboarding" },
  { id: 2, label: "Shop" },
  { id: 3, label: "Paywall" },
  { id: 9, label: "Payments" },
  { id: 4, label: "Distribution" },
  { id: 5, label: "AI Access" },
  { id: 6, label: "Landing" },
  { id: 7, label: "Booking" },
];

const CommunityScreen = ({ user }: Props) => {
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const communities = useAllCommunities({ communityId: user?.communityId });
  const allCommunities = useAllCommunities();
  const community = communities[0];
  const [formMode, setFormMode] = useState<string>("create");
  const [onboardingMode, setOnboardingMode] = useState<string>("create");
  const [shopMode, setShopMode] = useState<string>("create");
  const [paywallMode, setPaywallMode] = useState<string>("create");
  const { questionnaire } = useQuestionnaire({
    communityId: user?.communityId,
  });
  const plansResponse = useAllPlans({
    communityId: user?.communityId || "",
  });
  const shops = useAllShops({ communityId: user?.communityId });
  const shop = shops[0];
  const paywalls = useAllPaywalls({ communityId: user?.communityId });
  const paywall = paywalls[0];
  const distribution = useDistribution({ communityId: user?.communityId });
  const [distributionMode, setDistributionMode] = useState<string>("create");
  // Deep link support: admin.tribed.io/#/settings?tab=distribution opens that
  // tab directly (used by the tribed.io/guides upload links). The app runs on
  // a HashRouter, so the query lives inside location.hash, not location.search.
  const [currentTabIndex, setTabIndex] = useState(() => {
    const hashQuery = window.location.hash.split("?")[1] || "";
    const tab = new URLSearchParams(hashQuery).get("tab") || "";
    const match = TABS.find(
      (t) => t.label.toLowerCase() === tab.toLowerCase()
    );
    return match ? match.id : 0;
  });

  useEffect(() => {
    setFormMode(community ? "edit" : "create");
  }, [community]);

  useEffect(() => {
    setOnboardingMode(questionnaire ? "edit" : "create");
  }, [questionnaire]);

  useEffect(() => {
    setShopMode(shop ? "edit" : "create");
  }, [shop]);

  useEffect(() => {
    setPaywallMode(paywall ? "edit" : "create");
  }, [paywall]);

  useEffect(() => {
    setDistributionMode(distribution ? "edit" : "create");
  }, [distribution]);

  useEffect(() => {
    if (!snackbarMessage) return;
    setIsSnackbarOpen(true);
  }, [snackbarMessage]);

  const onCreateCommunity = async (community: Community) => {
    await createCommunity({
      ...community,
      id: community.id || user?.communityId || community.id,
    });
    setSnackbarMessage("Community saved");
  };

  const onEditCommunity = async (community: Community) => {
    await updateCommunity({
      ...community,
      id: community.id || user?.communityId || community.id,
    });
    setSnackbarMessage("Community updated");
  };

  const onRemoveCommunity = async ({ id }) => {
    await deleteCommunity(id);
    setSnackbarMessage("Community removed");
  };

  const onRemoveCurrentCommunity = () => {
    if (!community?.id) return;
    onRemoveCommunity({ id: community.id });
    setFormMode("create");
  };

  const onSaveOnboarding = async (data: Questionnaire) => {
    if (!user?.communityId) return;
    await saveQuestionnaire({
      communityId: user.communityId,
      questionnaire: data,
    });
    setSnackbarMessage("Onboarding saved");
  };

  const onEditOnboarding = async (data: Questionnaire) => {
    if (!user?.communityId) return;
    await saveQuestionnaire({
      communityId: user.communityId,
      questionnaire: data,
    });
    setSnackbarMessage("Onboarding updated");
  };

  const onRemoveOnboarding = async () => {
    if (!user?.communityId) return;
    await removeQuestionnaire(user.communityId);
    setOnboardingMode("create");
    setSnackbarMessage("Onboarding removed");
  };

  const onUploadOnboarding = async (data: Questionnaire) => {
    if (!user?.communityId) return;
    await uploadQuestionnaire(user.communityId, data);
    setSnackbarMessage("Onboarding uploaded");
  };

  // The shop writes go straight to Firestore and are gated by canEdit in
  // firestore.rules. A denied write now rejects instead of being swallowed, so
  // each handler has to report it. Without this the save looks like it worked.
  const shopErrorMessage = (error: any): string => {
    if (error?.code === "permission-denied") {
      return "You do not have permission to edit this shop.";
    }
    return "The shop could not be saved. Try again.";
  };

  const onCreateShop = async (shop: Shop) => {
    try {
      await createShop({
        ...shop,
        id: shop.id || user?.communityId || shop.id,
      });
      setSnackbarMessage("Shop saved");
    } catch (error) {
      console.error("Error creating shop", error);
      setSnackbarMessage(shopErrorMessage(error));
    }
  };

  const onEditShop = async (shop: Shop) => {
    try {
      await updateShop({
        ...shop,
        id: shop.id || user?.communityId || shop.id,
      });
      setSnackbarMessage("Shop updated");
    } catch (error) {
      console.error("Error updating shop", error);
      setSnackbarMessage(shopErrorMessage(error));
    }
  };

  const onRemoveShop = async ({ id }) => {
    try {
      await deleteShop(id);
      setSnackbarMessage("Shop removed");
    } catch (error) {
      console.error("Error removing shop", error);
      setSnackbarMessage(shopErrorMessage(error));
    }
  };

  const onRemoveCurrentShop = () => {
    if (!shop?.id) return;
    onRemoveShop({ id: shop.id });
    setShopMode("create");
  };

  const onCreatePaywall = async (
    paywall: SubscriptionLandingInfo & { id?: string }
  ) => {
    await createPaywall({
      ...paywall,
      id: paywall.id || user?.communityId || paywall.id,
    });
    setSnackbarMessage("Paywall saved");
  };

  const onEditPaywall = async (
    paywall: SubscriptionLandingInfo & { id?: string }
  ) => {
    await updatePaywall({
      ...paywall,
      id: paywall.id || user?.communityId || paywall.id,
    });
    setSnackbarMessage("Paywall updated");
  };

  const onRemovePaywall = async ({ id }) => {
    await deletePaywall(id);
    setSnackbarMessage("Paywall removed");
  };

  const onRemoveCurrentPaywall = () => {
    if (!user?.communityId) return;
    onRemovePaywall({ id: user.communityId });
    setPaywallMode("create");
  };

  const onSaveDistribution = async (
    input: Partial<Distribution> & { id: string; updatedBy: string }
  ) => {
    await updateDistribution(input);
    setSnackbarMessage("Distribution settings saved");
  };

  const renderSnackbar = () => (
    <Snackbar
      onCloseSnackbar={() => setIsSnackbarOpen(false)}
      isOpen={isSnackbarOpen}
      message={snackbarMessage}
      error={null}
    />
  );

  const renderCommunityTab = () => (
    <Card padding="lg">
      <CommunityForm
        onRemoveItem={onRemoveCurrentCommunity}
        onCreateItem={onCreateCommunity}
        onEditItem={onEditCommunity}
        onChageFormToEditMode={() => setFormMode("edit")}
        mode={formMode}
        item={(community || {}) as Community}
        communities={allCommunities}
        communityId={user?.communityId}
        isActive={currentTabIndex === 0}
      />
    </Card>
  );

  const renderOnboardingTab = () => (
    <Card padding="lg">
      <OnboardingForm
        onRemoveItem={onRemoveOnboarding}
        onCreateItem={onSaveOnboarding}
        onEditItem={onEditOnboarding}
        onChageFormToEditMode={() => setOnboardingMode("edit")}
        onUploadItem={onUploadOnboarding}
        mode={onboardingMode}
        item={(questionnaire || {}) as Questionnaire}
        plans={plansResponse.data.plans}
        plansLoading={plansResponse.loading}
        plansError={plansResponse.error}
        isActive={currentTabIndex === 1}
      />
    </Card>
  );

  const renderShopTab = () => (
    <Card padding="lg">
      <ShopForm
        onRemoveItem={onRemoveCurrentShop}
        onCreateItem={onCreateShop}
        onEditItem={onEditShop}
        onChageFormToEditMode={() => setShopMode("edit")}
        mode={shopMode}
        item={(shop || {}) as Shop}
        communityId={user?.communityId}
        isActive={currentTabIndex === 2}
      />
    </Card>
  );

  const renderPaywallTab = () => (
    <Card padding="lg">
      <PaywallForm
        onRemoveItem={onRemoveCurrentPaywall}
        onCreateItem={onCreatePaywall}
        onEditItem={onEditPaywall}
        onChageFormToEditMode={() => setPaywallMode("edit")}
        mode={paywallMode}
        item={(paywall || {}) as SubscriptionLandingInfo & { id?: string }}
        communityId={user?.communityId}
        community={community}
        isActive={currentTabIndex === 3}
      />
    </Card>
  );

  const renderDistributionTab = () => (
    <Card padding="lg">
      <DistributionForm
        item={distribution}
        communityId={user?.communityId}
        updatedBy={user?.id ?? ""}
        mode={distributionMode}
        isActive={currentTabIndex === 4}
        onChageFormToEditMode={() => setDistributionMode("edit")}
        onSaveMetadata={onSaveDistribution}
      />
    </Card>
  );

  const renderAIAccessTab = () => (
    <Card padding="lg">
      <AIAccessForm
        communityId={user?.communityId}
        isActive={currentTabIndex === 5}
      />
    </Card>
  );

  const renderLandingTab = () => (
    <Card padding="lg">
      <LandingForm
        communityId={user?.communityId}
        community={community}
        landingInfo={paywall}
        isActive={currentTabIndex === 6}
      />
    </Card>
  );

  const renderBookingTab = () => (
    <Card padding="lg">
      <BookingForm
        communityId={user?.communityId}
        isActive={currentTabIndex === 7}
      />
    </Card>
  );

  const renderBrandTab = () => (
    <Card padding="lg">
      <BrandForm
        communityId={user?.communityId}
        isActive={currentTabIndex === 8}
      />
    </Card>
  );

  return (
    <DesignSystemSurface>
      <Page>
        <PageInner>
          <PageHeader
            title="Settings"
            subtitle="Community, brand, onboarding, shop, and paywall."
          />
          <TabBar role="tablist">
            {TABS.map((tab) => (
              <TabButton
                key={tab.id}
                type="button"
                role="tab"
                $active={currentTabIndex === tab.id}
                aria-selected={currentTabIndex === tab.id}
                onClick={() => setTabIndex(tab.id)}
              >
                {tab.label}
              </TabButton>
            ))}
          </TabBar>
          {currentTabIndex === 0 && renderCommunityTab()}
          {currentTabIndex === 1 && renderOnboardingTab()}
          {currentTabIndex === 2 && renderShopTab()}
          {currentTabIndex === 3 && renderPaywallTab()}
          {currentTabIndex === 4 && renderDistributionTab()}
          {currentTabIndex === 5 && renderAIAccessTab()}
          {currentTabIndex === 6 && renderLandingTab()}
          {currentTabIndex === 7 && renderBookingTab()}
          {currentTabIndex === 8 && renderBrandTab()}
          {currentTabIndex === 9 && <StripePaymentsPanel key={user.communityId} communityId={user.communityId} canEdit={Number(user.role) <= 201} />}
          {renderSnackbar()}
        </PageInner>
      </Page>
    </DesignSystemSurface>
  );
};

const mapStateToProps = (state) => ({
  user: state.storeUser,
});

export default connect(mapStateToProps, null)(CommunityScreen);
